import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { MapPin, Video, Users, CheckCircle2, Globe, Mail, Phone, Calendar, Loader2, ArrowLeft, Share2, Facebook, Linkedin, MessageSquare, Link2, Twitter } from "lucide-react";

const SITE = process.env.REACT_APP_PUBLIC_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "");

export default function TherapistProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    api.get(`/therapists/profile/${id}`)
      .then(({ data }) => { setT(data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [id]);

  const seoTitle = t ? `${t.name}${t.credentials ? ", " + t.credentials : ""} | Find a Therapist — Sisterhood+` : "Find a Therapist — Sisterhood+";
  const seoDesc = t ? (t.bio || `${t.name} — a culturally responsive therapist in the Sisterhood+ directory, welcoming women from all backgrounds.`) : "";
  useSeo({ title: seoTitle, description: seoDesc, path: `/therapists/${id}`, image: t?.photo || "/og-therapists.png" });

  const shareKey = (t && t.slug) || id;
  const shareLink = `${SITE}/api/therapists/share/${shareKey}`;
  const shareText = t ? `Meet ${t.name} on the Sisterhood+ therapist directory` : "Sisterhood+ therapist directory";
  const copyShare = () => { navigator.clipboard?.writeText(shareLink); toast.success("Shareable link copied."); };
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`;
  const xUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(shareText)}`;
  const smsUrl = `sms:?&body=${encodeURIComponent(shareText + " " + shareLink)}`;

  const modality = t ? (t.modality || (t.telehealth ? "telehealth" : "")).toLowerCase() : "";
  const booking = t && (t.booking_url || t.website);

  return (
    <div className="min-h-screen bg-[#F7F2EA]">
      <header className="sticky top-0 z-10 bg-[#F7F2EA]/90 backdrop-blur border-b border-[#291B2D]/5">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/therapists" className="font-heading text-xl text-[#291B2D]">Sisterhood<span className="text-[#C89B55]">+</span></Link>
          {user ? (
            <a href="/home" className="text-sm bg-[#291B2D] text-white rounded-full px-4 py-2">Go to app</a>
          ) : (
            <div className="flex items-center gap-2 sm:gap-3">
              <a href="/therapists/apply" className="hidden sm:inline text-sm text-[#C89B55] hover:underline font-medium" data-testid="profile-are-you-therapist-link">Are you a therapist?</a>
              <a href="/get-started" className="text-sm bg-[#291B2D] text-white rounded-full px-4 py-2">Join Sisterhood+</a>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="therapist-profile-page">
        <Link to="/therapists" className="inline-flex items-center gap-2 text-sm text-[#291B2D]/60 hover:text-[#291B2D] mb-4"><ArrowLeft size={14} /> All therapists</Link>

        {status === "loading" && <div className="text-center py-20 text-[#291B2D]/45"><Loader2 className="inline animate-spin" size={22} /></div>}
        {status === "notfound" && (
          <div className="bg-white rounded-3xl p-10 text-center card-soft" data-testid="profile-notfound">
            <p className="font-heading text-xl">Therapist not found</p>
            <Link to="/therapists" className="text-sm text-[#C89B55] hover:underline mt-2 inline-block">Back to the directory</Link>
          </div>
        )}

        {status === "ok" && t && (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft">
            <div className="flex items-start gap-5 flex-wrap">
              {t.photo ? (
                <img src={mediaUrl(t.photo)} alt={t.name} className="w-24 h-24 rounded-3xl object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-3xl bg-[#291B2D] text-[#C89B55] flex items-center justify-center font-heading text-3xl">{(t.name || "T").slice(0, 1)}</div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-3xl text-[#291B2D] leading-tight">{t.name}</h1>
                {t.credentials && <p className="text-sm text-[#291B2D]/55 mt-1">{t.credentials}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(modality === "both" || modality === "telehealth") && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#C89B55]/15 text-[#291B2D] rounded-full px-2 py-0.5"><Video size={10} /> Telehealth</span>}
                  {(modality === "both" || modality === "in-person") && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#F7F2EA] text-[#291B2D]/70 rounded-full px-2 py-0.5"><MapPin size={10} /> In-person</span>}
                  {t.faith_informed && <span className="text-[10px] uppercase tracking-wide bg-[#F7F2EA] text-[#291B2D]/70 rounded-full px-2 py-0.5">Faith-informed</span>}
                  {t.trauma_informed && <span className="text-[10px] uppercase tracking-wide bg-[#F7F2EA] text-[#291B2D]/70 rounded-full px-2 py-0.5">Trauma-informed</span>}
                </div>
              </div>
              <button onClick={copyShare} data-testid="profile-share" className="inline-flex items-center gap-2 rounded-full bg-[#F7F2EA] text-[#291B2D] px-4 py-2 text-sm font-medium hover:brightness-95"><Share2 size={14} /> Share</button>
            </div>

            {t.bio && <p className="text-[15px] text-[#291B2D]/75 mt-5 leading-relaxed whitespace-pre-line">{t.bio}</p>}

            {!!(t.specialties || []).length && (
              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-widest text-[#291B2D]/45 font-semibold mb-2">Specialties</p>
                <div className="flex flex-wrap gap-1.5">{t.specialties.map((s) => <span key={s} className="text-xs bg-[#291B2D]/5 text-[#291B2D]/70 rounded-full px-3 py-1">{s}</span>)}</div>
              </div>
            )}

            <div className="mt-5 space-y-1.5 text-sm text-[#291B2D]/65">
              {!!(t.states || []).length && <p className="flex items-center gap-2"><MapPin size={14} /> Licensed in {t.states.join(", ")}</p>}
              {!!(t.languages || []).length && <p className="flex items-center gap-2"><Globe size={14} /> {t.languages.join(", ")}</p>}
              {!!(t.insurance || []).length && <p className="flex items-center gap-2"><CheckCircle2 size={14} /> {t.insurance.join(", ")}</p>}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {booking && <a href={booking} target="_blank" rel="noreferrer" data-testid="profile-book" className="inline-flex items-center gap-1.5 bg-[#291B2D] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:brightness-110"><Calendar size={14} /> Book / Website</a>}
              {t.email && <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1.5 bg-[#F7F2EA] text-[#291B2D] rounded-full px-5 py-2.5 text-sm font-medium hover:brightness-95"><Mail size={14} /> Email</a>}
              {t.phone && <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1.5 bg-[#F7F2EA] text-[#291B2D] rounded-full px-5 py-2.5 text-sm font-medium hover:brightness-95"><Phone size={14} /> Call</a>}
            </div>

            <div className="mt-6 pt-5 border-t border-[#291B2D]/8" data-testid="profile-share-row">
              <p className="text-[11px] uppercase tracking-widest text-[#291B2D]/45 font-semibold mb-2">Share this profile</p>
              <div className="flex flex-wrap items-center gap-2">
                <a href={fbUrl} target="_blank" rel="noreferrer" aria-label="Share on Facebook" data-testid="share-facebook" className="w-10 h-10 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center text-[#291B2D]"><Facebook size={16} /></a>
                <a href={liUrl} target="_blank" rel="noreferrer" aria-label="Share on LinkedIn" data-testid="share-linkedin" className="w-10 h-10 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center text-[#291B2D]"><Linkedin size={16} /></a>
                <a href={xUrl} target="_blank" rel="noreferrer" aria-label="Share on X" data-testid="share-x" className="w-10 h-10 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center text-[#291B2D]"><Twitter size={16} /></a>
                <a href={smsUrl} aria-label="Share via text" data-testid="share-sms" className="w-10 h-10 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center text-[#291B2D]"><MessageSquare size={16} /></a>
                <button onClick={copyShare} data-testid="share-copy" className="inline-flex items-center gap-1.5 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] px-4 h-10 text-sm text-[#291B2D]"><Link2 size={15} /> Copy link</button>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-[#291B2D]/50 text-center pt-6">
          Sisterhood+ lists independent providers and is not a referral service or medical provider. Verify credentials with your state board before booking.
        </p>
      </div>
    </div>
  );
}
